import { useState } from "react";
import AppIcon from "@/components/AppIcon";
import { useNavigate } from "react-router-dom";
import { X, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StaircaseLoader from "@/components/StaircaseLoader";

export const FREE_SAMPLE_DISMISS_KEY = "sms_free_sample_dismissed";

export default function FreeSampleModal({ open, onClose, initialGoal = "" }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [goal, setGoal] = useState(initialGoal);
  const [dontShow, setDontShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  if (!open) return null;

  const dismiss = () => {
    if (dontShow) {
      try { localStorage.setItem(FREE_SAMPLE_DISMISS_KEY, "1"); } catch { /* ignore */ }
    }
    onClose?.();
  };

  const tryFree = async () => {
    if (!goal.trim()) { toast.error("Type what you'd like to learn first"); return; }
    setBusy(true);
    setProgress(0);
    try {
      const { data } = await api.post("/free-sample", { goal: goal.trim() });
      const id = data.id;
      for (let i = 0; i < 160; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data: st } = await api.get(`/manuals/${id}/status`);
          setProgress(st.progress ?? 0);
          if (st.status === "ready") { await refresh(); navigate(`/manual/${id}`); return; }
          if (st.status === "error") { toast.error(st.error || "Could not create your sample. Try again."); setBusy(false); return; }
        } catch { /* keep polling */ }
      }
      toast.error("This is taking longer than usual — check your History in a moment.");
      setBusy(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create your free sample.";
      toast.error(msg, { duration: 6000 });
      setBusy(false);
      // If the freebie is spent/unavailable, close so they see the packs.
      if (err?.response?.status === 403) { await refresh(); onClose?.(); }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/45 backdrop-blur-sm"
      onClick={busy ? undefined : dismiss}
      data-testid="free-sample-overlay"
    >
      <div
        className="w-full max-w-md sms-card p-6 relative sms-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="free-sample-modal"
      >
        {!busy && (
          <button
            data-testid="free-sample-close-btn"
            onClick={dismiss}
            className="absolute top-3.5 right-3.5 p-1.5 rounded-full hover:bg-[#eef3ea] text-[#b7b3c8] hover:text-[#6b6880] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}

        {busy ? (
          <div className="py-6 flex flex-col items-center text-center">
            <h2 className="text-xl font-extrabold mb-1">Building your free manual…</h2>
            <p className="text-sm text-[#6b6880] mb-6">Hang tight — this takes about a minute.</p>
            <StaircaseLoader progress={progress} label={progress < 60 ? "Writing your steps…" : progress < 92 ? "Creating your cover photo…" : "Finishing up…"} />
          </div>
        ) : (
          <>
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 mx-auto"
              style={{ background: "linear-gradient(160deg,#7ba46f,#4f7746)", boxShadow: "0 10px 22px -10px rgba(79, 119, 70,.7)" }}
            >
              <Gift size={24} color="#fff" />
            </div>
            <h2 className="text-2xl font-extrabold leading-tight text-center" data-testid="free-sample-title">
              Your first{" "}
              <span className="whitespace-nowrap">
                <span style={{ color: "#171426" }}>Step</span>
                <span style={{ color: "#5d8a52" }}>My</span>
                <span style={{ color: "#171426" }}>Step</span>
              </span>{" "}
              Manual is <span className="text-[#5d8a52]">Free!</span>
            </h2>
            <p className="text-sm text-[#6b6880] mt-1.5 leading-relaxed text-center">
              A little taste, on us. Type anything you want to learn and we'll build a real
              manual with photos — <span className="font-semibold text-[#4a475c]">free, one time</span>. No charge.
            </p>

            <textarea
              data-testid="free-sample-goal-input"
              rows={2}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              maxLength={500}
              placeholder="e.g. How to make cold brew coffee"
              className="sms-input w-full px-4 py-3 text-base resize-none leading-snug min-h-[72px] mt-4"
              style={{ border: "2px solid #171426" }}
            />

            <button
              data-testid="free-sample-submit-btn"
              onClick={tryFree}
              className="sms-gradient-btn w-full text-white font-bold text-lg py-3.5 rounded-2xl mt-4 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(160deg,#7ba46f,#4f7746)" }}
            >
              <AppIcon size={22} /> Try it free
            </button>

            <div className="flex items-center justify-between mt-4">
              <label className="flex items-center gap-2 text-xs font-semibold text-[#9a96ac] cursor-pointer select-none">
                <input
                  data-testid="free-sample-dontshow"
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#5d8a52]"
                />
                Don't show this again
              </label>
              <button
                data-testid="free-sample-dismiss-btn"
                onClick={dismiss}
                className="text-sm font-semibold text-[#9a96ac] hover:text-[#5d8a52] transition-colors"
              >
                Maybe later
              </button>
            </div>

            <p className="text-[11px] text-[#b7b3c8] mt-3 flex items-center gap-1">
              <Sparkles size={11} className="text-[#c9c3e0]" /> One free sample per account.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
